import { useState, useRef, useEffect } from "react";
import { useMeta } from "@/hooks/useMeta";
import { motion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, Search, ChevronDown, ArrowRight, LocateFixed, Loader2, Clock, Building2, Sparkles, Users, Heart, Store } from "lucide-react";
import { getRecentlyViewed, type RecentItem } from "@/lib/recentlyViewed";
import { Link, useLocation } from "wouter";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import { useParallax } from "@/hooks/useParallax";
import { TiltCard } from "@/components/TiltCard";
import { AnimatedText } from "@/components/AnimatedText";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { SplitText } from "@/components/SplitText";
import { FloatingParticles } from "@/components/FloatingParticles";
import { ScrollProgressBar } from "@/components/ScrollProgressBar";

const CITY_LIST = [
  "Agra","Alwar","Bangalore","Bareilly","Bikaner","Chennai","Dehradun",
  "Delhi","Faridabad","Ghaziabad","Goa","North Goa","South Goa","Gurgaon","Hyderabad","Jaipur",
  "Jaisalmer","Jhansi","Jodhpur","Kanpur","Leh","Lucknow","Manali",
  "Meerut","Mumbai","Noida","Prayagraj","Ramnagar","Rishikesh","Shimla","Udaipur","Varanasi",
];

const SERVICE_LIST = [
  { label: "Wedding Planner", value: "wedding-planner" },
  { label: "Photography", value: "photography" },
  { label: "Makeup Artist", value: "makeup-artist" },
  { label: "Catering", value: "catering" },
  { label: "Decorator", value: "decorator" },
  { label: "Entertainment", value: "entertainment" },
  { label: "Music / DJ", value: "music" },
];

function matchToCity(raw: string): string {
  const r = raw.toLowerCase();
  const aliases: Record<string, string> = {
    mumbai: "Mumbai", bombay: "Mumbai",
    delhi: "Delhi", "new delhi": "Delhi", gurugram: "Gurgaon", gurgaon: "Gurgaon",
    noida: "Noida", faridabad: "Faridabad", ghaziabad: "Ghaziabad",
    bangalore: "Bangalore", bengaluru: "Bangalore",
    jaipur: "Jaipur", jaisalmer: "Jaisalmer", jodhpur: "Jodhpur",
    alwar: "Alwar", bikaner: "Bikaner", ajmer: "Jaipur",
    chennai: "Chennai", madras: "Chennai",
    hyderabad: "Hyderabad", secunderabad: "Hyderabad",
    goa: "Goa", panaji: "Goa",
    udaipur: "Udaipur",
    lucknow: "Lucknow", kanpur: "Kanpur", agra: "Agra",
    varanasi: "Varanasi", prayagraj: "Prayagraj", allahabad: "Prayagraj",
    bareilly: "Bareilly", jhansi: "Jhansi", meerut: "Meerut",
    dehradun: "Dehradun", rishikesh: "Rishikesh", mussoorie: "Dehradun",
    shimla: "Shimla", manali: "Manali", leh: "Leh",
    ramnagar: "Ramnagar",
  };
  for (const [key, val] of Object.entries(aliases)) {
    if (r.includes(key)) return val;
  }
  return "";
}

const Q = "w=600&q=80&auto=format&fit=crop";
const VENDOR_IMAGES: Record<string, string> = {
  "PHOTOGRAPHER":    `https://images.unsplash.com/photo-1606216794074-735e91aa2c92?${Q}`,
  "VIDEOGRAPHER":    `https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?${Q}`,
  "MAKEUP ARTIST":   `https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?${Q}`,
  "DECORATOR":       `https://images.unsplash.com/photo-1478146059778-26028b07395a?${Q}`,
  "DECOR":           `https://images.unsplash.com/photo-1478146059778-26028b07395a?${Q}`,
  "CATERER":         `https://images.unsplash.com/photo-1555244162-803834f70033?${Q}`,
  "DJ":              `https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?${Q}`,
  "MEHENDI":         `https://images.unsplash.com/photo-1601197405183-66c8ee1c4c5b?${Q}`,
  "CHOREOGRAPHER":   `https://images.unsplash.com/photo-1547153760-18fc86324498?${Q}`,
  "WEDDING PLANNERS":`https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?${Q}`,
  "FLORIST":         `https://images.unsplash.com/photo-1487530811015-780c2d55fe70?${Q}`,
  "JEWELLERY":       `https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?${Q}`,
  "CAKE":            `https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?${Q}`,
  "BAND":            `https://images.unsplash.com/photo-1514525253161-7a46d19cd819?${Q}`,
  "FIREWORKS":       `https://images.unsplash.com/photo-1467810563316-b5476525c0f9?${Q}`,
  "DEFAULT":         `https://images.unsplash.com/photo-1606216794074-735e91aa2c92?${Q}`,
};
const VENUE_IMAGES: Record<string, string> = {
  "RESORT":          `https://images.unsplash.com/photo-1571896349842-33c89424de2d?${Q}`,
  "HOTEL":           `https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?${Q}`,
  "BANQUET":         `https://images.unsplash.com/photo-1478146059778-26028b07395a?${Q}`,
  "BANQUET HALL":    `https://images.unsplash.com/photo-1478146059778-26028b07395a?${Q}`,
  "FARMHOUSE":       `https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?${Q}`,
  "PALACE":          `https://images.unsplash.com/photo-1582719508461-905c673771fd?${Q}`,
  "CLUB":            `https://images.unsplash.com/photo-1519225421980-715cb0215aed?${Q}`,
  "DEFAULT":         `https://images.unsplash.com/photo-1519167758481-83f550bb49b3?${Q}`,
};

export default function Home() {
  useMeta();
  const [, navigate] = useLocation();
  const [city, setCity] = useState("");
  const [eventType, setEventType] = useState("");
  const [service, setService] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoLat, setGeoLat] = useState<number | null>(null);
  const [geoLon, setGeoLon] = useState<number | null>(null);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [homeStats, setHomeStats] = useState({ vendors: 0, cities: 0, venues: 0 });

  useEffect(() => {
    let active = true;

    Promise.all([
      fetch("/api/vendors", { credentials: "include", cache: "no-store" })
        .then(res => res.ok ? res.json() : Promise.reject(new Error("Vendor API unavailable"))),
      fetch("/api/venues", { credentials: "include", cache: "no-store" })
        .then(res => res.ok ? res.json() : Promise.reject(new Error("Venue API unavailable"))),
    ]).then(([vendorData, venueData]) => {
      if (!active) return;
      const vendors = Array.isArray(vendorData?.vendors) ? vendorData.vendors : [];
      setHomeStats({
        vendors: vendors.length,
        cities: Number(venueData?.cities ?? 0),
        venues: Number(venueData?.total ?? venueData?.venues?.length ?? 0),
      });
    }).catch(error => {
      console.error("Failed to load homepage stats:", error);
    });

    return () => { active = false; };
  }, []);

  useEffect(() => {
    setRecentItems(getRecentlyViewed());
  }, []);

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setGeoLat(latitude);
        setGeoLon(longitude);
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "Accept-Language": "en" } }
          );
          const data = await res.json() as { address?: Record<string, string> };
          const addr = data.address ?? {};
          const raw = addr.city ?? addr.town ?? addr.village ?? addr.county ?? "";
          const matched = matchToCity(raw);
          if (matched) setCity(matched);
        } catch {
          // silent fallback
        } finally {
          setGeoLoading(false);
        }
      },
      () => { setGeoLoading(false); },
      { timeout: 8000 }
    );
  };

  const normalizeSearchCity = (value: string) => {
    const raw = value.trim();
    if (!raw) return "";
    const normalized = raw.toLowerCase();
    if (normalized.includes("goa")) return "Goa";
    return value;
  };

  const handleSearch = () => {
    const selectedCity = normalizeSearchCity(city);
    if (service === "venues") {
      const params = new URLSearchParams();
      if (selectedCity) params.set("city", selectedCity.toUpperCase());
      if (geoLat !== null) params.set("lat", geoLat.toFixed(4));
      if (geoLon !== null) params.set("lon", geoLon.toFixed(4));
      const qs = params.toString();
      navigate(`/venues${qs ? `?${qs}` : ""}`);
    } else {
      const params = new URLSearchParams();
      if (selectedCity) params.set("city", selectedCity);
      if (service) params.set("category", service);
      if (geoLat !== null) params.set("lat", geoLat.toFixed(4));
      if (geoLon !== null) params.set("lon", geoLon.toFixed(4));
      const qs = params.toString();
      navigate(`/vendors${qs ? `?${qs}` : ""}`);
    }
  };

  /* ── Parallax refs ── */
  const heroTextRef     = useRef<HTMLDivElement>(null);
  const heroVideoRef    = useParallax<HTMLDivElement>({ speed: 0.12,  scrub: 2 });

  /* ── GSAP scroll animation refs ── */
  const venueHeadRef    = useScrollAnimation<HTMLDivElement>({ type: "fadeUp", duration: 1 });
  const venueGridRef    = useStaggerAnimation<HTMLDivElement>({ type: "scaleIn", stagger: 0.08, start: "top 90%" });
  const inhouseHeadRef  = useScrollAnimation<HTMLDivElement>({ type: "fadeUp", duration: 1 });
  const inhouseGridRef  = useStaggerAnimation<HTMLDivElement>({ type: "fadeUp", stagger: 0.12 });
  const catHeadRef      = useScrollAnimation<HTMLDivElement>({ type: "fadeUp", duration: 1 });
  const catGridRef      = useStaggerAnimation<HTMLDivElement>({ type: "fadeUp", stagger: 0.08 });
  const dreamHeadRef    = useScrollAnimation<HTMLDivElement>({ type: "fadeUp", duration: 1 });
  const dreamGridRef    = useStaggerAnimation<HTMLDivElement>({ type: "scaleIn", stagger: 0.1 });
  const statsRef        = useStaggerAnimation<HTMLDivElement>({ type: "fadeUp", stagger: 0.15, start: "top 85%" });
  const testHeadRef     = useScrollAnimation<HTMLDivElement>({ type: "fadeUp", duration: 1 });
  const testGridRef     = useStaggerAnimation<HTMLDivElement>({ type: "fadeUp", stagger: 0.15 });
  const ctaTextRef      = useScrollAnimation<HTMLDivElement>({ type: "slideRight", duration: 1, start: "top 80%" });
  const ctaImgRef       = useScrollAnimation<HTMLDivElement>({ type: "imageReveal", duration: 1.2, start: "top 80%" });

  return (
    <div className="min-h-screen bg-[#f8f6f1] w-full overflow-x-hidden font-sans pb-mobile-nav lg:pb-0">
      <ScrollProgressBar />
      <Navbar />

      {/* SECTION 1: HERO */}
      <section className="hero-section relative isolate w-full min-h-[700px] sm:min-h-0 sm:aspect-[16/9] overflow-hidden z-0">
        {/* Video BG with parallax */}
        <div ref={heroVideoRef} className="absolute inset-0 z-0 scale-100 parallax-inner">
          <img
            src="/attached_assets/bookmysquadd-hero-final.png?v=20260913-2"
            alt="Luxury wedding and event venue"
            className="w-full h-full object-cover object-center hero-brand-image !opacity-100 !brightness-100"
            style={{ filter: "none", opacity: 1 }}
          />
          <div className="absolute inset-0 video-overlay" />
          <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.09) 0%, transparent 60%)'}} />
        </div>

        <div className="absolute inset-0 z-[1] opacity-20 pointer-events-none grain-overlay" />

        {/* Floating gold particles */}
        <div className="absolute inset-0 z-[2]">
          <FloatingParticles count={22} />
        </div>

        {/* Hero text — parallax upward on scroll */}
        <div ref={heroTextRef} className="relative z-10 h-full flex flex-col items-center justify-start px-6 text-center parallax-inner" style={{ paddingTop: 'clamp(105px, 12vh, 150px)', marginTop: '-64px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="mb-3 sm:mb-6 md:mb-8"
          >
            <div className="font-cinzel text-[9px] sm:text-xs tracking-[0.2em] sm:tracking-[0.4em] text-primary/80 uppercase mb-3 md:mb-4 px-2">
              
            </div>
            <div className="gold-line w-24 md:w-32 mx-auto mb-5 md:mb-8" />
          </motion.div>

          <h1 className="font-cormorant mb-3 sm:mb-5 md:mb-8 select-none" style={{ filter: "none", opacity: 1 }}>
            {/* "Plan your" — elegant light prelude */}
            <SplitText
              text="Plan your"
              mode="words"
              anim="load"
              delay={0.25}
              stagger={0.12}
              duration={1.1}
              className="block text-[34px] sm:text-[42px] md:text-[50px] lg:text-[56px] font-extralight text-[#fff8ed] tracking-[0.12em] sm:tracking-[0.16em] leading-[1.05] mb-0"
            />
            {/* "Dream Event" — italic for elegance */}
            <SplitText
              text="Dream Event"
              mode="words"
              anim="load"
              delay={0.45}
              stagger={0.12}
              duration={1.1}
              className="block text-[34px] sm:text-[42px] md:text-[50px] lg:text-[56px] font-extralight italic text-[#fff8ed] tracking-[0.08em] sm:tracking-[0.1em] leading-[1.15] mb-0"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="!text-white !opacity-100 text-[13px] leading-[1.35] sm:text-lg md:text-xl font-light max-w-xs sm:max-w-md md:max-w-xl mx-auto mb-3 md:mb-4 tracking-wide drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] px-2"
            style={{
              fontFamily: "'Gabriola', 'Cormorant Garamond', serif",
             color: '#fff8ed',
              textShadow: '0 2px 18px rgba(0,0,0,0.9), 0 0 18px rgba(212,175,55,0.30)',
            }}
          >
            Crafting premium celebrations with India's finest venues, vendors and bespoke event designers.
          </motion.p>

          {/* 4-field Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="hero-search w-full max-w-[900px] mt-0 mb-0"          
            >
            <div className="search-controls bg-[#17130e]/90 border border-white/25 p-1.5 flex flex-col lg:flex-row gap-1.5 gold-border-glow">
              {/* City */}
              <div className="flex-1 flex items-center px-3 py-2 border-b lg:border-b-0 lg:border-r border-white/10 gap-2">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <select
                  className="flex-1 !bg-transparent border-none outline-none text-white/80 text-sm font-manrope font-light cursor-pointer min-w-0"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                >
                  <option value="" className="bg-[#0d0b08]">
                    {geoLoading ? "Detecting…" : "Select City"}
                  </option>
                  {CITY_LIST.map(c => (
                    <option key={c} value={c} className="bg-[#0d0b08]">{c}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={detectLocation}
                  title="Use my location"
                  className="shrink-0 text-primary/50 hover:text-primary transition-colors duration-200"
                >
                  {geoLoading ? (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}>
                      <Loader2 className="w-3.5 h-3.5" />
                    </motion.div>
                  ) : (
                    <LocateFixed className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              {/* Event Type */}
              <div className="flex-1 flex items-center px-3 py-2 border-b lg:border-b-0 lg:border-r border-black/10 min-w-0">
                <Search className="w-4 h-4 text-primary mr-3 shrink-0" />
                <select
                  className="w-full !bg-transparent border-none outline-none text-white/80 text-sm font-manrope font-light cursor-pointer"
                  value={eventType}
                  onChange={e => setEventType(e.target.value)}
                >
                  <option value="" className="bg-[#0d0b08]">Event Type</option>
                  {["WEDDING","DESTINATION WEDDING","LIVE CONCERT","DEVOTIONAL EVENTS","AWARD SHOW","FASHION SHOW","EXHIBITION / EXPO","FARES","BABY SHOWER","HOUSE WARMING","BDAY PARTY"].map(t => (
                    <option key={t} value={t.toLowerCase().replace(/ /g,"-")} className="bg-[#0d0b08]">{t}</option>
                  ))}
                </select>
              </div>
              {/* Date */}
              <div className="flex-1 flex items-center px-3 py-2 border-b lg:border-b-0 lg:border-r border-black/10 min-w-0">
                <Calendar className="w-4 h-4 text-primary mr-3 shrink-0" />
                <input type="date" className="w-full !bg-transparent border-none outline-none text-white/60 text-sm font-manrope font-light !font-normal" />
              </div>
              {/* Service */}
              <div className="flex-1 flex items-center px-3 py-2 min-w-0">
                <Search className="w-4 h-4 text-primary mr-3 shrink-0" />
                <select
                  className="w-full !bg-transparent border-none outline-none text-white/80 text-sm font-manrope font-light cursor-pointer"
                  value={service}
                  onChange={e => setService(e.target.value)}
                >
                  <option value="" className="bg-[#0d0b08]">Select Service</option>
                  <option value="venues" className="bg-[#0d0b08]">Venues</option>
                  {SERVICE_LIST.map(s => (
                    <option key={s.value} value={s.value} className="bg-[#0d0b08]">{s.label}</option>
                  ))}
                </select>
              </div>
              {/* CTA */}
              <button
  onClick={handleSearch}
  className="w-full lg:w-auto !h-[30px] !min-h-[30px] !max-h-[30px] !px-6 !py-0 self-center max-md:mt-2 max-md:mb-2 flex items-center justify-center bg-[#c39a9d] text-[#241d1c] border-2 border-[#c39a9d] font-serif italic font-semibold text-[24px] normal-case hover:bg-[#b88d90] transition-all duration-300 shrink-0 box-border leading-none" style={{ fontSize: "32px" }}
>
  Search
</button>
            </div>

            {/* Popular tags */}
            <div className="hero-popular-tags !bg-transparent flex flex-wrap items-center justify-center gap-1">
              <span className="relative z-30 font-manrope text-[9px] tracking-[0.12em] text-white uppercase mr-1 px-0 py-0 bg-transparent">Popular:</span>
              {[
                { label: "Wedding Venues", href: "/venues" },
                { label: "Photography", href: "/vendors" },
                { label: "Bridal Makeup", href: "/vendors" },
                { label: "Entertainment", href: "/vendors" },
                { label: "Catering", href: "/vendors" },
              ].map(tag => (
                <Link
                  key={tag.label}
                  href={tag.href}
                  className="px-3 py-1 text-[10px] font-manrope text-white/95 bg-black/20 border border-white/45 rounded-full hover:bg-black/35 hover:border-white/70 cursor-pointer transition-all duration-300"
                >
                  {tag.label}
                </Link>
              ))}
            </div>

          </motion.div>
        </div>

      </section>

      {/* SECTION 3: POPULAR VENUE SEARCHES */}
      <section className="relative z-20 pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12" style={{background: '#f8f6f1', position: 'relative', zIndex: 20, isolation: 'isolate'}}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-5 md:mb-8 text-center opacity-100 translate-y-0">
            <p className="font-cinzel text-[13px] sm:text-[15px] tracking-[0.3em] sm:tracking-[0.45em] text-primary/80 uppercase mb-2">✦ Curated For You ✦</p>
            <div className="gold-line w-16 mx-auto mb-2" />
            <h2
              className="curated-venue-heading font-cormorant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal relative z-50"
              style={{ color: "#2b241c", opacity: 1, visibility: "visible", display: "block" }}
            >
              Popular <span className="text-primary italic font-semibold">Venue</span> Searches
            </h2>
          </div>

          {/* Bento grid */}
          <div ref={venueGridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-[310px_310px] gap-4">

            {/* Large left card — spans 2 rows */}
            <Link href="/venues" className="md:col-span-2 lg:col-span-1 lg:row-span-2">
              <div
                className="relative overflow-hidden group cursor-pointer h-[420px] md:h-[380px] lg:h-full w-full transition-transform duration-500 hover:scale-[1.015]"
              >
                <img
                  src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=900&q=85"
                  alt="Luxury Hotels"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/15 group-hover:via-black/35 transition-all duration-600" />
                <div className="absolute inset-0 border border-white/[0.06] group-hover:border-primary/45 transition-all duration-500" />
                <div className="absolute inset-x-0 top-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "linear-gradient(90deg, transparent, #d4af37, transparent)" }} />
                <div className="venue-card-copy absolute bottom-8 left-8 right-8 z-20">
                 <span style={{ color: '#fffdf8' }} className="font-cinzel text-[10px] tracking-[0.28em] uppercase block mb-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"> 5-Star Hotels</span>
                  <h3 style={{ color: '#fffdf8' }} className="font-cormorant text-[1.55rem] md:text-[1.8rem] font-semibold leading-[1.08] mb-3 drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"> Luxury 5-Star Wedding Hotels
                  </h3>
                 <p style={{ color: '#fffdf8' }} className="font-manrope text-sm font-light leading-relaxed mb-5 drop-shadow-[0_1px_8px_rgba(0,0,0,0.8)]"> Grand ballrooms, rooftop terraces and pool-side lawns in India's finest 5-star hotel properties.
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-5">
                    {["Mumbai", "Bangalore", "Delhi", "Pune", "Hyderabad"].map(c => (
                      <span key={c} style={{ color: '#fffdf8' }} className="font-manrope text-[10px] border-b border-white/25 pb-0.5 group-hover:text-[#fff8ed] group-hover:border-white/40 transition-colors duration-300"> {c}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
                    <span className="font-cinzel text-[9px] tracking-[0.3em] text-primary uppercase">Explore All</span>
                    <span className="text-primary text-sm">→</span>
                  </div>
                </div>
              </div>
            </Link>

            {/* Four equal small cards */}
            {[
              {
                img: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=85",
                type: "Banquet Halls",
                title: "Premium Banquet Halls",
                cities: ["Mumbai", "Delhi", "Jaipur"],
              },
              {
                img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=85",
                type: "Gardens & Lawns",
                title: "Marriage Gardens & Lawns",
                cities: ["Bangalore", "Pune", "Ahmedabad"],
              },
              {
                img: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=85",
                type: "Destination Resorts",
                title: "Luxury Destination Resorts",
                cities: ["Goa", "Udaipur", "Shimla"],
              },
              {
                img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=85",
                type: "Heritage Farmhouses",
                title: "Royal Heritage Farmhouses",
                cities: ["Delhi NCR", "Jaipur", "Chandigarh"],
              },
            ].map((card, i) => (
              <Link key={i} href="/venues">
                <div
                  className="relative overflow-hidden group cursor-pointer h-[240px] md:h-[260px] lg:h-full w-full transition-transform duration-500 hover:scale-[1.02]"
                >
                  <img
                    src={card.img}
                    alt={card.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/92 via-black/30 to-black/5 group-hover:via-black/20 transition-all duration-600" />
                  <div className="absolute inset-0 border border-white/[0.05] group-hover:border-primary/40 transition-all duration-500" />
                  <div className="absolute inset-x-0 top-0 h-[1.5px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: "linear-gradient(90deg, transparent, #d4af37, transparent)" }} />
                  <div className="absolute bottom-5 left-5 right-5">
                    <span className="font-cinzel text-[8px] tracking-[0.4em] text-primary/60 uppercase block mb-1.5">{card.type}</span>
                    <h3 className="wedding-story-title font-cormorant text-[1.3rem] text-white font-semibold leading-tight mb-3" style={{ textTransform: "none", whiteSpace: "normal", overflow: "visible", lineHeight: 1.08 }}>{card.title}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {card.cities.map(c => (
                          <span key={c} className="font-manrope text-[9px] text-white/65 border-b border-white/20 pb-0.5 group-hover:text-white/85 group-hover:border-white/35 transition-colors duration-300">{c}</span>
                        ))}
                      </div>
                      <span className="text-primary/50 group-hover:text-primary text-base transition-colors duration-300 ml-2 shrink-0">→</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Second row — 3 more venue types */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                img: "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&q=85",
                type: "Heritage",
                title: "Palace & Heritage Venues",
                desc: "Palaces and havelis steeped in royal Indian grandeur.",
                cities: ["Jaipur", "Udaipur", "Jodhpur"],
              },
              {
                img: "https://images.pexels.com/photos/2403017/pexels-photo-2403017.jpeg?auto=compress&cs=tinysrgb&w=1200",
                type: "Poolside",
                title: "Poolside & Lawn Venues",
                desc: "Lush lawns and shimmering poolside settings under open skies.",
                cities: ["Goa", "Mumbai", "Pune"],
              },
              {
                img: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=85",
                type: "Hilltop",
                title: "Scenic Hilltop Retreats",
                desc: "Breathtaking mountain backdrops for intimate celebrations.",
                cities: ["Shimla", "Mussoorie", "Ooty"],
              },
            ].map((card, i) => (
              <Link key={`extra-${i}`} href="/venues">
                <div className="relative overflow-hidden group cursor-pointer h-[220px] w-full transition-transform duration-500 hover:scale-[1.02]">
                  <img src={card.img} alt={card.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/92 via-black/30 to-black/5 group-hover:via-black/20 transition-all duration-600" />
                  <div className="absolute inset-0 border border-white/[0.05] group-hover:border-primary/40 transition-all duration-500" />
                  <div className="absolute inset-x-0 top-0 h-[1.5px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: "linear-gradient(90deg, transparent, #d4af37, transparent)" }} />
                  <div className="absolute bottom-5 left-5 right-5">
                    <span className="font-cinzel text-[8px] tracking-[0.4em] text-primary/75 uppercase block mb-1.5">{card.type}</span>
                    <h3 className="wedding-story-title font-cormorant text-xl text-white font-semibold leading-tight mb-2" style={{ textTransform: "none", whiteSpace: "normal", overflow: "visible", lineHeight: 1.08 }}>{card.title}</h3>
                    <p className="font-manrope text-[11px] text-white/70 mb-2 leading-relaxed">{card.desc}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {card.cities.map(c => (
                        <span key={c} className="font-manrope text-[9px] text-white/60 border-b border-white/20 pb-0.5">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: INHOUSE SERVICES */}
      <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#0a0804]">
        <div className="max-w-7xl mx-auto">
          <div ref={inhouseHeadRef} className="mb-2 md:mb-4 text-center">
            <p className="font-cinzel text-[13px] sm:text-[15px] tracking-[0.3em] sm:tracking-[0.45em] text-primary/80 uppercase mb-4">✦ Bespoke Offerings ✦</p>
            <div className="gold-line w-16 mx-auto mb-3" />
            <h2
              className="signature-services-heading font-cormorant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal relative z-50"
              style={{ color: "#fff8ed", opacity: 1, visibility: "visible", display: "block" }}
            >
              <span className="text-[#2b241c] font-normal not-italic">
                Signature
              </span>{" "}
              <span className="italic font-semibold text-[#b18445]">
                Services
              </span>
            </h2>
          </div>

          <div ref={inhouseGridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              { img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=700&q=80", title: "Wedding Planning", desc: "End-to-end meticulous planning by our luxury expert team", href: "/vendors" },
              { img: "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=700&q=80", title: "Photography & Films", desc: "Cinematic captures of your most treasured moments", href: "/vendors" },
              { img: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=700&q=80", title: "Bridal Artistry", desc: "Top makeup artists curating your flawless special day look", href: "/vendors" },
            ].map((svc, i) => (
              <Link key={i} href={svc.href}>
                <TiltCard className="h-full" max={5} glare>
                  <div className="luxury-card glass-card p-6 flex flex-col group cursor-pointer h-full rounded-sm overflow-hidden">
                    <div className="w-full aspect-square mb-6 overflow-hidden border border-primary/20 group-hover:border-primary/50 transition-colors">
                      <img src={svc.img} alt={svc.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-108" />
                    </div>
                    <div className="flex flex-col flex-1 text-center items-center justify-center">
                      <h3 className="font-cormorant text-white text-3xl font-semibold mb-3 normal-case leading-tight mb-3">{svc.title}</h3>
                      <p className="font-manrope text-white/75 font-light text-sm mb-6">{svc.desc}</p>
                      <span className="mt-auto font-cinzel text-primary text-[10px] tracking-[0.2em] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all uppercase">
                        Discover More <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </TiltCard>
              </Link>
            ))}
          </div>
        </div>
      </section>

{/* SECTION 5: BROWSE BY CATEGORY */}
<section className="relative z-0 pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">
  <div className="max-w-7xl mx-auto">
    <div ref={catHeadRef} className="flex flex-col items-center mb-2 md:mb-4 text-center">
      <div className="w-full max-w-3xl mx-auto">
        <p className="font-cinzel text-[11px] sm:text-[13px] tracking-[0.3em] sm:tracking-[0.45em] text-primary/80 uppercase mb-4">
          ✦ Vendor Categories ✦
        </p>
        <div className="gold-line w-16 mx-auto mb-3" />
        <h2
              className="explore-categories-heading font-cormorant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal relative z-50"
              style={{ color: "#2b241c", opacity: 1, visibility: "visible", display: "block" }}
            >
          Explore <span className="text-primary italic font-semibold">Categories</span>
        </h2>
      </div>

      <Link
        href="/vendors"
        className="hidden md:inline-flex items-center gap-2 font-cinzel text-[10px] tracking-[0.2em] uppercase text-primary font-semibold hover:text-white transition-colors mt-0 md:mt-2"
      >
        Explore All 19 Categories <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
          <div ref={catGridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Venues",        num: "01", image: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=700&q=80", sub: ["Lawn", "Farmhouse", "Banquet", "Hotel", "Resort"],                   href: "/venues" },
              { name: "Makeup",        num: "02", image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=700&q=80", sub: ["Bridal Makeup", "Groom Makeup", "Party Makeup"],                     href: "/vendors" },
              { name: "Catering",      num: "03", image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=700&q=80", sub: ["Indian/Traditional", "Chinese", "Jain Food", "Punjabi", "South Indian"], href: "/vendors" },
              { name: "Photography",   num: "04", image: "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=700&q=80", sub: ["Pre-Wedding", "Wedding", "Corporate", "Brand Shoot"],                href: "/vendors" },
              { name: "Decorator",     num: "05", image: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=700&q=80", sub: ["Wedding Decorator", "Baby Party", "Balloon", "Theme Decorator"],    href: "/vendors" },
              { name: "Entertainment", num: "06", image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=700&q=80", sub: ["DJ", "Comedian", "Magician", "Tarot Card Reader"],                   href: "/vendors" },
            ].map((vendor, i) => (
              <Link key={i} href={vendor.href}>
                <div className="group relative overflow-hidden rounded-sm h-[400px] cursor-pointer border border-transparent hover:gold-border-glow transition-all duration-500">
                  <img src={vendor.image} alt={vendor.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20 group-hover:from-black/95 transition-colors duration-500" />
                  <div className="absolute top-6 right-6 font-cinzel text-xl text-primary/80 font-bold opacity-80 group-hover:opacity-100 group-hover:text-primary transition-all">
                    {vendor.num}
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h4 className="font-cormorant text-3xl font-semibold text-white leading-tight mb-4 group-hover:mb-2 transition-all duration-300">{vendor.name}</h4>
                    <div className="flex flex-wrap gap-2 max-h-0 opacity-0 overflow-hidden group-hover:max-h-32 group-hover:opacity-100 group-hover:mt-4 transition-all duration-500 ease-out">
                      {vendor.sub.map((sub, j) => (
                        <span key={j} className="text-[9px] font-cinzel uppercase tracking-wider font-semibold text-primary bg-[#17130e]/85 border border-primary/30 backdrop-blur-md px-2.5 py-1 rounded-sm">
                          {sub}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center md:hidden">
            <Link href="/vendors" className="inline-flex items-center gap-2 font-cinzel text-[10px] tracking-[0.2em] uppercase text-primary font-semibold hover:text-white transition-colors">
              Explore All 19 Categories <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* RECENTLY VIEWED */}
      {recentItems.length > 0 && (
        <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#0a0804] border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8 md:mb-10">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-primary/70" />
                <p className="font-cinzel text-[10px] sm:text-[11px] tracking-[0.35em] text-primary/70 uppercase">Recently Viewed</p>
              </div>
              <div className="flex items-center gap-6">
                <Link href="/vendors" className="font-cinzel text-[9px] tracking-[0.2em] uppercase text-white/40 hover:text-primary transition-colors">
                  All Vendors
                </Link>
                <Link href="/venues" className="font-cinzel text-[9px] tracking-[0.2em] uppercase text-white/40 hover:text-primary transition-colors">
                  All Venues
                </Link>
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {recentItems.map((item, idx) => {
                const img = item.type === "venue"
                  ? VENUE_IMAGES[item.venueType?.toUpperCase() ?? ""] ?? VENUE_IMAGES["DEFAULT"]
                  : VENDOR_IMAGES[item.category?.toUpperCase() ?? ""] ?? VENDOR_IMAGES["DEFAULT"];
                const href = item.type === "vendor"
                  ? `/vendors?search=${encodeURIComponent(item.name)}`
                  : `/venues?search=${encodeURIComponent(item.name)}`;
                const badge = item.type === "venue" ? (item.venueType ?? "Venue") : (item.category ?? "Vendor");

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.07 }}
                  >
                    <Link href={href} className="block group relative overflow-hidden rounded-sm border border-white/8 hover:border-primary/40 transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
                      {/* Image */}
                      <div className="relative h-36 sm:h-44 overflow-hidden">
                        <img
                          src={img}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#f8f6f1] via-[#f8f6f1]/30 to-transparent" />
                        {/* Type badge */}
                        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-white/10 px-2 py-0.5">
                          {item.type === "venue"
                            ? <Building2 className="w-2.5 h-2.5 text-primary/80" />
                            : <Sparkles className="w-2.5 h-2.5 text-primary/80" />
                          }
                          <span className="font-cinzel text-[7px] tracking-[0.18em] uppercase text-primary/90">{item.type}</span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="px-3 py-3 bg-[#0f0c08]">
                        <p className="font-cinzel text-[10px] tracking-[0.15em] text-primary/60 uppercase truncate mb-1">{badge}</p>
                        <p className="font-cormorant text-base text-[#fff8ed] font-semibold leading-tight truncate">{item.name}</p>
                        {item.city && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <MapPin className="w-2.5 h-2.5 text-white/30 shrink-0" />
                            <span className="font-manrope text-[11px] text-white/45 truncate capitalize">{item.city.toLowerCase()}</span>
                          </div>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 6: DREAM WEDDINGS */}
        <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">        <div className="max-w-7xl mx-auto">
          <div ref={dreamHeadRef} className="text-center mb-2 md:mb-4">
            <div className="max-w-3xl mx-auto">
              <p className="font-cinzel text-[13px] sm:text-[15px] tracking-[0.3em] sm:tracking-[0.45em] text-primary/80 uppercase mb-4">✦ Inspiration ✦</p>
              <div className="gold-line w-16 mx-auto mb-3" />
              <h2
              className="dream-weddings-heading font-cormorant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal relative z-50 normal-case"
              style={{ color: "#2b241c", opacity: 1, visibility: "visible", display: "block" }}
            >
                Dream <span className="text-primary italic font-semibold">Weddings</span>
              </h2>
            </div>
            <Link href="/weddings" className="inline-flex justify-center w-full md:w-auto">
              <Button variant="outline" className="hidden md:flex rounded-sm border-primary/50 text-primary font-cinzel text-[10px] tracking-[0.2em] uppercase hover:bg-primary hover:text-black transition-colors mx-auto mt-2">
                View All Galleries
              </Button>
            </Link>
          </div>

        <div
          ref={dreamGridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:auto-rows-[250px]"
          >
                        {[
              {
                img: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=800&q=85",
                title: "Royal Rajasthani",
                names: "Priya & Rahul",
                city: "Udaipur",
                style: "Destination",
                span: "lg:col-span-2 lg:row-span-2",
                aspect: "",
              },
{
 img: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=700&q=85",
                title: "Pink City Magic",
                names: "Ananya & Vikram",
                city: "Jaipur",
                style: "Traditional",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=700&q=85",
                title: "Beachside Boho",
                names: "Neha & Arjun",
                city: "Goa",
                style: "Bohemian",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=700&q=85",
                title: "Modern Luxury",
                names: "Shriya & Karan",
                city: "Mumbai",
                style: "Contemporary",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=700&q=85",
                title: "Garden Soirée",
                names: "Riya & Dev",
                city: "Bangalore",
                style: "Garden",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=700&q=85",
                title: "Grand Reception",
                names: "Kavya & Aditya",
                city: "Delhi",
                style: "Contemporary",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=700&q=85",
                title: "Royal Banquet",
                names: "Simran & Arjun",
                city: "Jaipur",
                style: "Traditional",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.pexels.com/photos/25742763/pexels-photo-25742763/free-photo-of-creative-pre-wedding-photoshoot-fotographiya.jpeg?auto=compress&cs=tinysrgb&w=800",
                title: "Heritage Wedding",
                names: "Meera & Rohan",
                city: "Udaipur",
                style: "Heritage",
                span: "",
                aspect: "aspect-square",
              },
              {
                img: "https://images.pexels.com/photos/169214/pexels-photo-169214.jpeg?auto=compress&cs=tinysrgb&w=800",
                title: "Sunset Ceremony",
                names: "Aisha & Ishaan",
                city: "Goa",
                style: "Coastal",
                span: "",
                aspect: "aspect-square",
              },
            ].map((wedding, i) => (
              <Link key={i} href="/weddings" className={wedding.span}>
                <div 
                className="group cursor-pointer h-full">
                  <div className={`overflow-hidden relative ${
                    wedding.aspect
                    } ${wedding.span ? 'h-full' : ''} border border-white/5 group-hover:border-primary/40 transition-colors duration-500`}
                    >
                    <img
                      src={wedding.img}
                      alt={wedding.title.replace(/\b\w/g, c => c.toUpperCase()).toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="font-cinzel text-[9px] tracking-[0.2em] uppercase text-primary bg-white/90 border border-primary/30 px-2 py-1 backdrop-blur-sm">
                        {wedding.style}
                      </span>
                    </div>
                    <div className="absolute bottom-5 left-5 right-5">
                      <h3 className="dream-card-title font-cormorant text-2xl md:text-4xl font-semibold tracking-normal text-white/80 mb-1">{wedding.title}</h3>
                      <p className="font-cormorant italic text-xl md:text-2xl text-white/90 mb-1 leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]">{wedding.names}</p>
                      <p className="font-manrope text-xs text-white/65 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-primary/50 shrink-0" /> {wedding.city}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-center md:hidden">
            <Link href="/weddings">
              <Button variant="outline" className="rounded-sm border-primary/50 text-primary font-cinzel text-[10px] tracking-[0.2em] uppercase hover:bg-primary hover:text-black transition-colors w-full">
                View All Galleries
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 6B: MOMENTS WE CREATED */}
      <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(212,175,55,0.05) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-5 md:mb-8">
            <div className="max-w-3xl mx-auto">
              <p className="font-cinzel text-[11px] sm:text-[13px] tracking-[0.3em] sm:tracking-[0.5em] text-primary/70 uppercase mb-4">✦ Our Work ✦</p>
              <div className="gold-line w-16 mx-auto mb-3" />
              <h2 className="moments-heading font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal italic leading-[0.9]">
                Moments<br />
              </h2>
            </div>
            <p className="font-poppins text-white/45 text-sm leading-relaxed max-w-xl mx-auto mt-2">
              Every celebration is a story. Every photograph, a memory sealed in gold. We don't plan events — we craft chapters that last forever.
            </p>
          </div>

          {/* Asymmetric 4-image grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" style={{ height: "clamp(360px, 55vw, 680px)" }}>
            <div className="md:row-span-2 overflow-hidden relative group img-zoom border border-white/10 hover:border-primary/40 transition-colors duration-500">
              <img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=90" className="w-full h-full object-cover brightness-95" alt="Royal Wedding" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <span className="font-cinzel text-[8px] tracking-[0.3em] uppercase text-primary bg-white/90 px-2.5 py-1 border border-primary/40 backdrop-blur-sm">Royal Wedding</span>
                <p className="font-cormorant italic text-2xl text-white mt-3 leading-tight drop-shadow-lg">Udaipur Palace</p>
              </div>
            </div>
            <div className="overflow-hidden relative group img-zoom border border-white/10 hover:border-primary/40 transition-colors duration-500">
              <img src="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=900&q=90" className="w-full h-full object-cover brightness-100" alt="Floral Décor" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3">
                <span className="font-cinzel text-[7px] tracking-[0.28em] uppercase text-primary bg-white/90 px-2 py-1 border border-primary/40 backdrop-blur-sm">Floral Décor</span>
              </div>
            </div>
            <div className="overflow-hidden relative group img-zoom border border-white/10 hover:border-primary/40 transition-colors duration-500">
              <img src="https://images.unsplash.com/photo-1537633552985-df8429e8048b?w=900&q=90" className="w-full h-full object-cover brightness-100" alt="Photography" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3">
                <span className="font-cinzel text-[7px] tracking-[0.28em] uppercase text-primary bg-white/90 px-2 py-1 border border-primary/40 backdrop-blur-sm">Photography</span>
              </div>
            </div>
            <div className="col-span-2 md:col-span-2 overflow-hidden relative group img-zoom border border-white/10 hover:border-primary/40 transition-colors duration-500">
              <img src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1400&q=90" className="w-full h-full object-cover brightness-100" alt="Destination Wedding" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <span className="font-cinzel text-[7px] tracking-[0.28em] uppercase text-primary bg-white/90 px-2.5 py-1 border border-primary/40 backdrop-blur-sm">Destination Wedding</span>
                  <p className="font-cormorant italic text-2xl text-white mt-2 drop-shadow-lg">Goa Beach Ceremony</p>
                </div>
                <Link href="/events">
                  <button className="font-cinzel text-[9px] tracking-[0.2em] uppercase px-4 py-2.5 border border-primary/70 text-primary hover:bg-primary hover:text-black transition-all duration-300 backdrop-blur-sm">
                    View Story
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Impact numbers */}
          <div className="mt-6 grid grid-cols-3 border border-white/8">
            {[
              { num: "0",  label: "Events Created",     note: "Across India & abroad" },
              { num: "0%", label: "Client Satisfaction", note: "Verified post-event surveys" },
              { num: "0+", label: "Lives Touched",       note: "Couples, families & guests" },
            ].map((s, i) => (
              <div key={i} className={`py-4 md:py-5 px-2 md:px-4 text-center ${i < 2 ? "border-r border-white/8" : ""}`}>
                <div className="font-playfair text-3xl md:text-4xl text-primary italic mb-1">{s.num}</div>
                <div className="font-cinzel text-[9px] tracking-[0.2em] text-white uppercase mb-1">{s.label}</div>
                <div className="font-poppins text-xs text-white/30">{s.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link href="/events">
              <button className="px-12 py-4 border border-primary/50 text-primary font-cinzel font-semibold text-[10px] tracking-[0.28em] uppercase hover:bg-primary hover:text-black transition-all duration-300 inline-flex items-center gap-3">
                Explore Our Full Portfolio <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 7: STATS COUNTER */}
      <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.08)_0%,transparent_60%)] pointer-events-none" />
        {/* Ambient video overlay */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none overflow-hidden">
          <video autoPlay muted loop playsInline className="w-full h-full object-cover scale-110">
            <source src="https://assets.mixkit.co/videos/preview/mixkit-golden-confetti-falling-1-large.mp4" type="video/mp4" />
          </video>
        </div>
        <div ref={statsRef} className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { num: homeStats.vendors.toLocaleString("en-IN"), label: "Verified Vendors", icon: Store },
              { num: homeStats.cities.toLocaleString("en-IN"), label: "Cities Covered", icon: MapPin },
              { num: "—", label: "Happy Couples", icon: Heart },
              { num: homeStats.venues.toLocaleString("en-IN"), label: "Wedding Venues", icon: Building2 },
            ].map((stat, i) => (
              <TiltCard key={stat.label} max={4} scale={1.02} glare>
                <div className="glass-gold rounded-sm px-4 py-5 md:px-5 md:py-6 flex flex-col items-center justify-center text-center gap-2 h-full">
                  <stat.icon className="w-5 h-5 text-primary/70 mb-1" aria-hidden="true" />
                  {stat.num === "—" ? (
                    <div className="text-3xl md:text-4xl font-cinzel text-shimmer leading-none">—</div>
                  ) : (
                    <AnimatedCounter
                      value={stat.num}
                      duration={1.6}
                      className="text-3xl md:text-4xl font-cinzel text-shimmer leading-none counter-pulse"
                    />
                  )}
                  <div className="font-manrope text-white/65 text-[10px] uppercase tracking-[0.2em] font-medium">{stat.label}</div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7B: HOW IT WORKS */}
      <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.05)_0%,transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-2 md:mb-4">
            <p className="font-cinzel text-[11px] sm:text-[13px] tracking-[0.3em] sm:tracking-[0.45em] text-primary/80 uppercase mb-4">✦ The Journey ✦</p>
            <div className="gold-line w-16 mx-auto mb-3" />
            <h2
              className="our-partners-heading font-cormorant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal relative z-10"
              style={{ color: "#2b241c", opacity: 1, visibility: "visible", display: "block" }}
            >
              How It <span className="text-primary italic font-semibold">Works</span>
            </h2>
            <p className="font-manrope text-white/72 text-base font-light mt-4 max-w-lg mx-auto leading-relaxed">
              From your first search to your last dance — we make every step effortless.
            </p>
          </div>

          <div className="relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
              {[
                {
                  num: "01",
                  icon: "🔍",
                  title: "Search & Discover",
                  desc: "Browse 6,346+ verified vendors and 78 curated venues across 4 destinations.",
                  color: "#d4af37",
                },
                {
                  num: "02",
                  icon: "❤️",
                  title: "Compare & Shortlist",
                  desc: "Read genuine reviews, compare packages side-by-side, and save your favourites.",
                  color: "#e8a4c8",
                },
                {
                  num: "03",
                  icon: "📋",
                  title: "Book & Secure",
                  desc: "Select your package, confirm the date, and pay a refundable ₹2,000 advance in minutes.",
                  color: "#50e3c2",
                },
                {
                  num: "04",
                  icon: "🎊",
                  title: "Celebrate in Style",
                  desc: "Sit back and let India's finest wedding professionals create your unforgettable day.",
                  color: "#4ade80",
                },
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center group">
                  {/* Step number circle */}
                  <div className="relative mb-8">
                    <div
                      className="w-24 h-24 rounded-full flex items-center justify-center relative border transition-all duration-500 group-hover:scale-105"
                      style={{ backgroundColor: step.color + "12", borderColor: step.color + "40", boxShadow: `0 0 40px ${step.color}15` }}
                    >
                      <span className="text-3xl">{step.icon}</span>
                      <div
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center font-cinzel text-[10px] font-bold"
                        style={{ backgroundColor: step.color, color: "#000" }}
                      >
                        {step.num}
                      </div>
                    </div>
                  </div>
                  <h3 className="font-cinzel text-sm tracking-[0.1em] uppercase mb-3" style={{ color: step.color }}>{step.title}</h3>
                  <p className="font-manrope text-white/72 text-sm leading-relaxed font-light max-w-xs">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-2 text-center">
            <Link href="/vendors">
              <button className="px-10 py-4 bg-primary text-black font-cinzel font-bold text-xs tracking-[0.25em] uppercase hover:bg-primary/90 transition-all duration-300 gold-glow rounded-sm">
                Start Your Search
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 9: TESTIMONIALS */}
        <section className="relative pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#0a0804] border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div ref={testHeadRef} className="mb-5 md:mb-8 text-center">
            <p className="font-cinzel text-[11px] sm:text-[13px] tracking-[0.3em] sm:tracking-[0.5em] text-primary/70 uppercase mb-4">✦ Client Words ✦</p>
            <div className="gold-line w-16 mx-auto mb-3" />
            <h2 className="testimonials-heading font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal italic leading-[0.9] mb-6">
              Turning Dreams<br />
              <span className="text-primary">Into Experiences</span>
            </h2>
            <p className="font-poppins text-white/40 text-base max-w-xl mx-auto leading-relaxed">
              Don't take our word for it — hear from the couples who trusted us with the most important day of their lives.
            </p>
          </div>

          <div ref={testGridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { text: "The planning was flawless. Every detail was curated with such precision and elegance. Our guests are still talking about the magnificent decor and seamless experience.", name: "Rohan & Sneha", date: "Married in Jaipur" },
              { text: "Finding vendors through BMS was the best decision. The platform's luxury partners delivered beyond our wildest expectations. A truly 5-star experience from start to finish.", name: "Vikram & Aisha", date: "Married in Udaipur" },
              { text: "They understood our vision for a minimalist yet opulent celebration immediately. The venue recommendation was breathtaking and the execution was pure perfection.", name: "Karan & Meera", date: "Married in Goa" }
            ].map((review, i) => (
              <TiltCard key={i} max={5} scale={1.02} glare>
                <div className="luxury-card glass-card p-10 flex flex-col items-center text-center rounded-sm relative overflow-hidden h-full">
                  <div className="font-cormorant text-8xl text-primary/20 absolute top-4 left-6 leading-none" style={{ fontSize: "32px" }}>"</div>
                  <div className="flex gap-1 mb-8 mt-4">
                    {[1,2,3,4,5].map(star => <span key={star} className="text-primary text-sm">★</span>)}
                  </div>
                  <p className="font-cormorant italic text-xl text-white/80 leading-relaxed mb-10 flex-grow relative z-10">{review.text}</p>
                  <div className="w-16 h-px bg-primary/40 mb-6" />
                  <h4 className="font-cinzel text-sm text-primary uppercase tracking-[0.1em] mb-1">{review.name}</h4>
                  <p className="font-manrope text-xs text-white/65 font-light">{review.date}</p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10: PARTNER BRANDS MARQUEE */}
<section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">        <div className="text-center mb-2 md:mb-4">
          <p className="font-cinzel text-sm tracking-[0.4em] text-primary font-semibold uppercase mb-4 gold-glow-text">✦ Trusted By India's Finest ✦</p>
          <h2
              className="our-partners-heading font-cormorant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal"
              style={{ color: "#2b241c", opacity: 1, visibility: "visible", display: "block" }}
            >
            Our <span className="text-primary italic font-semibold">Partners</span>
          </h2>
        </div>
        {[
          ["Taj Hotels","The Oberoi","ITC Hotels","The Leela","JW Marriott","Hyatt India","Rambagh Palace","Umaid Bhawan","Samode Palace","RAAS Hotels","Aman Resorts","Suryagarh"],
          ["Wilderness Resorts","Joseph Radhik","Tasveer Studios","Wizcraft Events","Percept Live","Ambika Pillai Studio","Mickey Contractor","E-Factor Events","The Park Hotels","Conrad Hotels","Shaadi Squad","Sabyasachi"],
        ].map((row, rowIdx) => (
          <div key={rowIdx} className="relative mb-4 last:mb-0">
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none" style={{ background: "linear-gradient(90deg,#0a0806,transparent)" }} />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none" style={{ background: "linear-gradient(270deg,#0a0806,transparent)" }} />
            <div className="marquee-outer">
              <div className={`marquee-track${rowIdx % 2 === 1 ? " marquee-reverse" : ""}`}>
                {[...row, ...row].map((brand, i) => (
                  <div key={i} className="marquee-item">
                    <span className="font-cinzel text-[11px] tracking-[0.28em] uppercase text-primary hover:text-primary/70 transition-colors duration-300 whitespace-nowrap cursor-default">
                      {brand}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* SECTION: WEDDING PLANNING TOOLS */}
        <section className="pt-0 md:pt-4 pb-6 md:pb-8 px-6 md:px-12 bg-[#f8f6f1] border-t border-white/5">        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <p className="font-cinzel text-[13px] sm:text-[15px] tracking-[0.35em] text-primary/80 uppercase mb-3">✦ Free Planning Tools ✦</p>
            <h2 className="planning-tools-heading font-cormorant text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-3">
              Plan Every <span className="text-primary italic font-semibold">Details</span>
            </h2>
            <p className="font-manrope text-white/40 text-sm max-w-xl mx-auto">
              Free tools to help you stay organised — from guest lists to budget tracking and personalised hashtags.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">            {[
              { href: "/guest-list", emoji: "👥", title: "Guest List Manager", desc: "Track invitations, RSVPs & plus-ones in one place" },
              { href: "/budget-calculator", emoji: "💰", title: "Budget Calculator", desc: "Plan every rupee across all event categories" },
              { href: "/hashtag-generator", emoji: "#", title: "Hashtag Generator", desc: "Create the perfect personalised event hashtag" },
            ].map(tool => (
              <Link key={tool.href} href={tool.href}>
                <div className="group bg-white/[0.03] border border-white/10 hover:border-primary/40 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:bg-white/[0.06]">
                  <div className="text-3xl mb-3">{tool.emoji}</div>
                  <h3 className="font-cinzel text-sm text-white mb-2 group-hover:text-primary transition-colors">{tool.title}</h3>
                  <p className="font-manrope text-white/40 text-xs leading-relaxed">{tool.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11: VENDOR CTA */}
      <section className="bg-[#f8f6f1] border-y border-white/10">
        <div className="flex flex-col md:flex-row">
          <div ref={ctaTextRef} className="w-full md:w-1/2 p-8 sm:p-12 md:p-24 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[100px] rounded-full pointer-events-none" />
            <p className="font-cinzel text-[11px] sm:text-[13px] tracking-[0.3em] sm:tracking-[0.45em] text-primary/80 uppercase mb-4">✦ Join The Network ✦</p>
            <h2 className="premium-vendor-heading font-cormorant text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal leading-tight mb-6 md:mb-8">
              Are you a <span className="text-primary italic font-semibold">Premium</span> Vendor?
            </h2>
            <ul className="space-y-3 md:space-y-4 mb-8 md:mb-8">
              {[
                "Showcase your portfolio to high-net-worth clients",
                "Receive verified, quality leads",
                "Join an exclusive network of luxury professionals"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 md:gap-4">
                  <div className="w-1 h-1 bg-primary rounded-full mt-2.5 shadow-[0_0_8px_rgba(212,175,55,0.8)] shrink-0" />
                  <span className="font-manrope text-white/70 font-light text-base md:text-lg">{item}</span>
                </li>
              ))}
            </ul>
            <Link href="/list-your-business">
              <button className="self-start px-8 py-4 bg-primary text-black font-cinzel font-bold text-xs tracking-[0.2em] uppercase hover:bg-primary/90 transition-all duration-300 gold-glow rounded-sm">
                List Your Business
              </button>
            </Link>
          </div>

          <div ref={ctaImgRef} className="w-full md:w-1/2 min-h-[400px] md:min-h-full relative">
            <img src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80" alt="Luxury Event" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#f8f6f1] to-transparent md:via-transparent md:to-transparent" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

